import {
  type AriaTooltipProps,
  type TooltipTriggerProps,
  useTooltip,
  useTooltipTrigger,
} from "@react-aria/tooltip";
import { mergeProps } from "@react-aria/utils";
import cn from "classnames";
import React, { type HTMLAttributes, useEffect, useRef } from "react";
import {
  type TooltipTriggerState,
  useTooltipTriggerState,
} from "react-stately";
// SCSS
import "./ToolTip.scss";
import { Overlay, useOverlayPosition } from "@react-aria/overlays";
// Interfaces
export interface TooltipProps {
  className?: string;
  /**
   * The direct child should be a button or link to be able to trigger the
   * tooltip
   */
  children: React.ReactNode;

  state?: TooltipTriggerState;
  /**
   * Ref of the tooltip
   */
  tooltipRef: React.RefObject<HTMLSpanElement | null>;
  /**
   * Ref of the element related to tooltip
   */
  targetRef: React.RefObject<HTMLElement | null>;
  /**
   * Position: top or bottom (default is bottom)
   */
  position?: "top" | "bottom";
}
const Tooltip = ({
  children,
  className,
  state,
  tooltipRef,
  targetRef,
  position = "bottom",
  ...props
}: TooltipProps & AriaTooltipProps) => {
  const { tooltipProps } = useTooltip(props, state);

  return (
    <span
      className={cn("ds-tooltip", className)}
      {...mergeProps(props, tooltipProps)}
      ref={tooltipRef}
      style={getTooltipPosition(targetRef, position)}
    >
      {children}
    </span>
  );
};

const getTooltipPosition = (
  targetRef: React.RefObject<HTMLElement | null>,
  position: "top" | "bottom",
) => {
  if (!targetRef || !targetRef.current) return undefined;

  let top: number, left: number;
  const OFFSET_HEIGHT = 7;
  const WIDTH = 150;
  // should use a ref to access the actual height, but tooltipRef is actually the overlayRef
  const tooltipHeight = 40;

  if (position === "top") {
    const targetRect = targetRef.current.getBoundingClientRect();
    top = targetRect.top - tooltipHeight - OFFSET_HEIGHT + window.scrollY;

    left = targetRect.left + window.scrollX;
    if (window.innerWidth - left < WIDTH) {
      left = targetRect.left - WIDTH;
    }

    return { top, left };
  } else {
    const targetRect = targetRef.current.getBoundingClientRect();
    top = targetRect.top + targetRect.height + OFFSET_HEIGHT + window.scrollY;
    left = targetRect.left + window.scrollX;
    if (window.innerWidth - left < WIDTH) {
      left = targetRect.left - WIDTH;
    }

    return { top, left };
  }
};

export interface TooltipWrappedProps {
  children: React.ReactNode;
  tooltip: React.ReactNode;
  tooltipClassname?: string;
  wrapper: "button" | "div" | "td";
  wrapperClassname?: string;
  className?: string;
  showOnFocus?: boolean;
  position?: "top" | "bottom";
}

const TooltipWrapper = ({
  children,
  tooltip,
  tooltipClassname,
  wrapperClassname,
  wrapper,
  className,
  showOnFocus,
  position,
  ...props
}: TooltipWrappedProps & TooltipTriggerProps & HTMLAttributes<HTMLElement>) => {
  const state = useTooltipTriggerState(props);
  const ref = React.useRef<HTMLElement>(null);
  const overlayRef = React.useRef<HTMLSpanElement>(null);
  const contentWrapper = { wrapper };

  // Get props for the trigger and its tooltip
  const { triggerProps, tooltipProps } = useTooltipTrigger(
    props,
    state,
    ref as React.RefObject<HTMLElement>,
  );
  const { overlayProps } = useOverlayPosition({
    targetRef: ref as React.RefObject<HTMLElement>,
    overlayRef: overlayRef as React.RefObject<Element>,
  });
  const toolTipWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleTouchOutside = (event: TouchEvent) => {
      if (
        toolTipWrapperRef.current &&
        !toolTipWrapperRef.current.contains(event.target as Node)
      ) {
        state.close();
      }
    };
    document.addEventListener("touchstart", handleTouchOutside);
    return () => {
      document.removeEventListener("touchstart", handleTouchOutside);
    };
  }, [state]);
  return (
    <div
      className={cn("ds-tooltip__container", className)}
      ref={toolTipWrapperRef}
    >
      <contentWrapper.wrapper
        // @ts-expect-error Weird contravariant stuff
        ref={ref}
        className={wrapperClassname}
        {...triggerProps}
        {...props}
        onFocus={() => {
          if (showOnFocus) {
            state.open();
          }
        }}
        onTouchStart={() => {
          if (!state.isOpen) {
            state.open();
          } else {
            state.close();
          }
        }}
      >
        {children}
      </contentWrapper.wrapper>
      {state.isOpen && (
        <Overlay {...overlayProps}>
          <Tooltip
            state={state}
            className={tooltipClassname}
            tooltipRef={overlayRef}
            targetRef={ref}
            position={position}
            {...tooltipProps}
          >
            {tooltip}
          </Tooltip>
        </Overlay>
      )}
    </div>
  );
};

export { Tooltip, TooltipWrapper };
